import convertCurrency from "~/lib/helpers/currencyConverter";
import { useCurrentCurrency } from "~/lib/helpers/CurrencyHelper";
import CircularProgressChart from "../Charts/CircularProgressBar";
import { Bar } from "react-chartjs-2";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "~/components/ui/drawer";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Plus } from "lucide-react";
import {
  Chart as ChartJS,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
} from "chart.js";

ChartJS.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip);

export function DeskSheetRow({ row, index, areaSummary }: { row: any; index: number; }) {

  const { currency } = useCurrentCurrency()

  const barData = (() => {
    const barValues = [
      { label: "Enterprise Suite", value: row.enterpriseSpacePrice || 0 },
      { label: "Private Office", value: row.privateSpacePrice || 0 },
      { label: "Sublet Office", value: row.subletSpacesPrice || 0 },
      { label: "Managed Space", value: row.managedSpacesPrice || 0 },
      { label: "Coworking Desk", value: row.coworkingSpacesPrice || 0 },
      { label: "Shared Office", value: row.sharedSpacesPrice || 0 },
      { label: "Virtual Office", value: row.virtualOfficePrice || 0 },
      { label: "Part time Desk", value: 0 },
    ];

    const sorted = barValues.sort((a, b) => b.value - a.value);

    const baseColors = [
      "#7CC6E4", "#609DDB", "#8E91E3", "#A183DC", "#B07DCC", "#CC79C6", "#E679B8", "#EC6DA9"
    ];

    return {
      labels: sorted.map(item => item.label),
      datasets: [
        {
          label: "Avg Price",
          data: sorted.map(item => item.value),
          backgroundColor: baseColors,
          borderRadius: 6,
        },
      ],
    };
  })();

  const barOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: function (context) {
          let tooltipEl = document.getElementById('chartjs-tooltip');

          // Create tooltip element if it doesn't exist
          if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'chartjs-tooltip';
            tooltipEl.style.background = 'white';
            tooltipEl.style.border = '1px solid #e5e7eb';
            tooltipEl.style.borderRadius = '8px';
            tooltipEl.style.boxShadow = '0 4px 10px rgba(0, 0, 0, 0.1)';
            tooltipEl.style.color = '#111827';
            tooltipEl.style.opacity = 1;
            tooltipEl.style.position = 'absolute';
            tooltipEl.style.pointerEvents = 'none';
            tooltipEl.style.padding = '6px 8px';
            tooltipEl.style.transition = 'all .1s ease';
            tooltipEl.style.font = 'sans-serif';
            document.body.appendChild(tooltipEl);
          }

          const tooltipModel = context.tooltip;

          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
          }

          if (tooltipModel.body) {
            const dataIndex = tooltipModel.dataPoints[0].dataIndex;
            const label = context.chart.data.labels?.[dataIndex];
            const value = context.chart.data.datasets[0].data[dataIndex];

            tooltipEl.innerHTML = `
              <div style="display: flex; flex-direction: column;">
                <span style="font-weight: 600; font-size:12px;">${label}</span>
                <span style="color: #6b7280; font-size:12px;">Avg Price: ${value}</span>
              </div>
            `;
          }

          const position = context.chart.canvas.getBoundingClientRect();
          tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
          tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
          tooltipEl.style.opacity = 1;
        }
      },
      datalabels: {
        anchor: 'start',
        align: 'end',
        rotation: -90,
        color: '#000',
        font: {
          family: 'Inter, ui-sans-serif, system-ui, sans-serif',
          size: 13,
          weight: '500',
        },
        formatter: (value: number, context: any) => {
          return barData.labels[context.dataIndex];
        },
        clamp: false,
      },
    },
    scales: {
      x: {
        ticks: {
          font: {
            family: 'Inter, ui-sans-serif, system-ui, sans-serif',
          },
          display: false,
        },
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
      },
    },
  };

  const isEven = index % 2 === 1;

  return (
    <Drawer>
      <DrawerTrigger
        className={`flex text-lg font-normal text-oh-black py-4 w-full ${isEven ? "bg-white" : "bg-gray-100"
          } border-t-1 border-b-2 border-gray-200`}
      >
        {/* Sticky Location Column */}
        <div
          className={`sticky left-0 ${isEven ? "bg-white" : "bg-gray-100"} min-w-40 w-40 max-w-40 flex items-center z-10 overflow-hidden whitespace-nowrap text-ellipsis pl-5`}>
          <Plus className="w-8 h-8 mr-2 flex-shrink-0 text-oh-black" />
          <span className="font-medium truncate">{row.suburb}</span>
        </div>


        {/* Price Columns */}
        <div className="flex text-oh-black [&>div]:min-w-40 [&>div]:w-40 [&>div]:max-w-40 [&>div]:px-2 [&>div]:overflow-hidden [&>div]:whitespace-nowrap [&>div]:text-ellipsis [&>div]:text-start pl-8">
          {[...row.areaProfileSummary, ...Array(5 - row.areaProfileSummary.length).fill(null)]
            .slice(0, 5)
            .map((i: any, idx: number) => (
              <div key={i?.sfid ?? `empty-${idx}`}>
                {i && i.deskMonthlyPrice > 0
                  ? convertCurrency(
                    Math.round(i.deskMonthlyPrice),
                    areaSummary?.currencyIsoCode,
                    currency
                  )
                  : "-"}
              </div>
            ))}
        </div>

      </DrawerTrigger>

      <DrawerContent className="bg-white px-6 max-h-[70vh]">
        <DrawerHeader>
          <DrawerTitle className="py-2 font-medium text-2xl">{areaSummary?.city || areaSummary?.suburb} Insights</DrawerTitle>
        </DrawerHeader>

        <div className="h-[80vh] overflow-auto">
          {/* Average Desk Price Section */}
          <div className="mb-4">
            <div className="text-sm pt-4 font-normal text-gray-400">AVG DESK PRICE</div>
            <div className="text-4xl font-bold text-cyan-500">
              {convertCurrency(row.deskMonthlyPrice, areaSummary?.currencyIsoCode, currency)}
            </div>
            <div className="flex mt-2 text-gray-700">
              <div>
                <div className="text-sm text-gray-400">LOWEST PRICE</div>
                <div className="text-xl font-medium text-oh-black">
                  {convertCurrency(row.minMonthlyPrice, areaSummary?.currencyIsoCode, currency)}
                </div>
              </div>
              <div className="ml-6">
                <div className="text-sm text-gray-400">HIGHEST PRICE</div>
                <div className="text-xl font-medium text-oh-black">
                  {convertCurrency(row.maxMonthlyPrice, areaSummary?.currencyIsoCode, currency)}
                </div>
              </div>
            </div>
          </div>

          {/* Listings Section */}
          <div className="space-y-4">
            {/* Private Offices */}
            <div className="flex justify-between items-center border-b pb-2">
              <div>
                <p className="font-medium text-gray-700">Private Offices</p>
                <p className="text-xs text-gray-400">Private Offices Listing in {row.suburb}</p>
              </div>
              <div className="w-10 flex justify-start">
                <div className="text-cyan-600 font-bold">{row.availableSpacesBySpacetype}</div>
              </div>
            </div>

            {/* Available Spaces */}
            <div className="flex justify-between items-center border-b pb-2">
              <div>
                <p className="font-medium text-gray-700">Available Spaces</p>
                <p className="text-xs text-gray-400">All Available Listing in Sydney</p>
              </div>
              <div className="w-10 flex justify-start">
                <span className="text-cyan-600 font-bold">{row.totalAvailableSpaces}</span>
              </div>
            </div>

            {/* A Grade Building */}
            <div className="flex justify-between items-center border-b pb-2">
              <div>
                <p className="font-medium text-gray-700">A Grade Building</p>
                <p className="text-xs text-gray-400">Listing in A Grade Building</p>
              </div>
              <CircularProgressChart value={Math.round(row.agradeBuildingPercentage || 0)} />
            </div>

            {/* Discounted Listing */}
            <div className="flex justify-between items-center pb-2">
              <div>
                <p className="font-medium text-gray-700">Discounted Listing</p>
                <p className="text-xs text-gray-400">Listing does have discounted price</p>
              </div>
              <CircularProgressChart value={Math.round(row.discountedListingsPercentage || 0)} />
            </div>
          </div>

          {/* Growth Summary Table */}
          <div className="mt-6">
            <div className="text-md font-semibold mb-2 text-start">Sydney Office Market: Price & Sales Insights</div>
            <div className="overflow-x-auto">
              <table className="min-w-full border-t-2 border-gray-200 text-sm text-left">
                <thead>
                  <tr>
                    <th className="py-3 font-medium">No. of Desks</th>
                    <th className="py-3 font-medium">Desk Price</th>
                    <th className="py-3 font-medium">Demand</th>
                    <th className="py-3 font-medium">Sale %</th>
                  </tr>
                </thead>
                <tbody>
                  {row.areaProfileSummary.map((d, i) => (
                    <tr key={i} className={i % 2 === 1 ? "" : "bg-gray-50"}>
                      <td className="py-3">{d.name}</td>
                      <td className="py-3">{convertCurrency(d.deskMonthlyPrice, areaSummary?.currencyIsoCode, currency)}</td>
                      <td className="py-3">{d.enquriesPercentage}</td>
                      <td className="py-3">{d.salesPercentage}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bar Chart */}
          <div className="mt-6">
            <div className="text-lg font-bold mb-2">Average Desk Price</div>
            <Bar data={barData} options={barOptions} plugins={[ChartDataLabels]} />
          </div>
        </div>

      </DrawerContent>
    </Drawer>
  );
}